import React from 'react'
import styled from 'styled-components'
import { Crown, Trophy, Medal, Award, Star, ArrowRight } from 'lucide-react'

const RankingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%);
    animation: backgroundAnimation 10s ease-in-out infinite alternate;
  }

  @keyframes backgroundAnimation {
    0% {
      transform: scale(1) rotate(0deg);
      opacity: 0.7;
    }
    100% {
      transform: scale(1.1) rotate(5deg);
      opacity: 0.9;
    }
  }
`

const RankingContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 500px;
  padding: 20px;
  text-align: center;
`

const Title = styled.h1`
  font-size: 48px;
  font-weight: 800;
  color: #FFD700;
  text-shadow: 
    2px 2px 4px rgba(0,0,0,0.5),
    0 0 20px rgba(255,215,0,0.3);
  margin-bottom: 20px;
  letter-spacing: 4px;
  text-transform: uppercase;
`

const Subtitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: white;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
  margin-bottom: 30px;
  opacity: 0.9;
`

const WinnerPodium = styled.div`
  display: flex;
  justify-content: center;
  align-items: end;
  gap: 20px;
  margin-bottom: 40px;
  padding: 20px;
`

const PodiumPlace = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  animation: ${props => `podiumRise${props.$place} 1s ease-out`};

  @keyframes podiumRise1 {
    0% {
      transform: translateY(100px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes podiumRise2 {
    0% {
      transform: translateY(100px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes podiumRise3 {
    0% {
      transform: translateY(100px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }
`

const WinnerAvatar = styled.div`
  width: ${props => props.$place === 1 ? '80px' : props.$place === 2 ? '70px' : '60px'};
  height: ${props => props.$place === 1 ? '80px' : props.$place === 2 ? '70px' : '60px'};
  border-radius: 50%;
  background: ${props => props.$bgColor || 'linear-gradient(135deg, #667eea, #764ba2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.$place === 1 ? '32px' : props.$place === 2 ? '28px' : '24px'};
  border: ${props => props.$place === 1 ? '4px solid #FFD700' : props.$place === 2 ? '3px solid #C0C0C0' : '3px solid #CD7F32'};
  box-shadow: 0 8px 25px rgba(0,0,0,0.3);
  margin-bottom: 10px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background: ${props => props.$place === 1 ? 'linear-gradient(135deg, #FFD700, #FFA500)' : 
                           props.$place === 2 ? 'linear-gradient(135deg, #C0C0C0, #A0A0A0)' : 
                                                'linear-gradient(135deg, #CD7F32, #B8860B)'};
    border-radius: 50%;
    z-index: -1;
    opacity: 0.3;
    animation: glow 2s infinite;
  }

  @keyframes glow {
    0%, 100% {
      transform: scale(1);
      opacity: 0.3;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.6;
    }
  }
`

const WinnerName = styled.div`
  font-size: ${props => props.$place === 1 ? '18px' : '16px'};
  font-weight: 700;
  color: white;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
  margin-bottom: 5px;
`

const WinnerScore = styled.div`
  font-size: ${props => props.$place === 1 ? '16px' : '14px'};
  font-weight: 600;
  color: #FFD700;
  background: rgba(255,255,255,0.2);
  padding: 4px 12px;
  border-radius: 15px;
  margin-bottom: 10px;
`

const PodiumBase = styled.div`
  width: 80px;
  height: ${props => props.$place === 1 ? '60px' : props.$place === 2 ? '45px' : '30px'};
  background: ${props => props.$place === 1 ? 'linear-gradient(135deg, #FFD700, #FFA500)' : 
                         props.$place === 2 ? 'linear-gradient(135deg, #C0C0C0, #A0A0A0)' : 
                                              'linear-gradient(135deg, #CD7F32, #B8860B)'};
  border-radius: 8px 8px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: 800;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
  position: relative;

  &::before {
    content: '${props => props.$place}';
    font-size: 20px;
    font-weight: 800;
  }
`

const CrownIcon = styled.div`
  position: absolute;
  top: -20px;
  color: #FFD700;
  animation: bounce 2s infinite;

  @keyframes bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }
`

const ContinueButton = styled.button`
  background: linear-gradient(135deg, #00d4ff, #00a6d6);
  color: white;
  border: none;
  padding: 15px 40px;
  border-radius: 30px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 6px 20px rgba(0,212,255,0.4);

  &:hover {
    background: linear-gradient(135deg, #00a6d6, #0086b3);
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0,212,255,0.6);
  }

  &:active {
    transform: translateY(-1px);
  }
`

const CongratsMessage = styled.div`
  color: white;
  font-size: 20px;
  font-weight: 600;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
  margin-bottom: 20px;
  opacity: 0.9;
  
  .highlight {
    color: #FFD700;
    font-weight: 800;
  }
`

const FinalRankingBoard = ({ players = [], onContinue }) => {
  // 기본 플레이어 데이터 (props가 없을 때)
  const defaultPlayers = [
    {
      id: 1,
      name: '이유준',
      score: 200000,
      avatar: '🏆',
      bgColor: 'linear-gradient(135deg, #FFD700, #FFA500)'
    },
    {
      id: 2,
      name: '박민지',
      score: 185000,
      avatar: '👑',
      bgColor: 'linear-gradient(135deg, #FF6B6B, #FF8E53)'
    },
    {
      id: 3,
      name: '최나',
      score: 170000,
      avatar: '🎮',
      bgColor: 'linear-gradient(135deg, #4ECDC4, #44A08D)'
    }
  ]

  const displayPlayers = players.length > 0 ? players.slice(0, 3) : defaultPlayers
  const winner = displayPlayers[0]

  const getRankIcon = (place) => {
    switch(place) {
      case 1: return <Crown size={24} />
      case 2: return <Trophy size={22} />
      case 3: return <Medal size={20} />
      default: return null
    }
  }

  return (
    <RankingContainer onClick={(e) => e.stopPropagation()}>
      <RankingContent>
        <Title>FINAL RANKING</Title>
        <Subtitle>게임 완료!</Subtitle>

        <CongratsMessage>
          🎉 축하합니다! <span className="highlight">{winner?.name}</span>님이 우승했습니다! 🎉
        </CongratsMessage>

        <WinnerPodium>
          {displayPlayers.map((player, index) => (
            <PodiumPlace key={player.id || index} $place={index + 1}>
              {index === 0 && (
                <CrownIcon>
                  <Crown size={32} />
                </CrownIcon>
              )}
              
              <WinnerAvatar 
                $place={index + 1} 
                $bgColor={player.bgColor}
              >
                {player.avatar}
              </WinnerAvatar>
              
              <WinnerName $place={index + 1}>
                {player.name}
              </WinnerName>
              
              <WinnerScore $place={index + 1}>
                {player.score.toLocaleString()}
              </WinnerScore>
              
              <PodiumBase $place={index + 1} />
            </PodiumPlace>
          ))}
        </WinnerPodium>

        <ContinueButton onClick={onContinue}>
          문제 상세보기
          <ArrowRight size={20} />
        </ContinueButton>
      </RankingContent>
    </RankingContainer>
  )
}

export default FinalRankingBoard